import { type FC, useCallback, useMemo, useRef, useState } from "react";

import { SwitchEntityTypes } from "enums/switchEntityTypes";
import { TemplateTypes } from "../../../enums";
import { getNotifyConfig } from "../../../features/SystemNotifications/helpers";
import { NotifyStepConfig, type NotifyStepConfirmPayload } from "../../../features/SystemNotifications/types";
import { type IObservable } from "../../../interfaces";
import { type NotifyByEmailController } from "../../notifyStep/notifyByEmailController";

import { tabs } from "components/notifyStep/NotifyStepSwitch/constants";
import ModalSizes from "../../../enums/modalSizes";
import useNotifyConfig from "../../../features/SystemNotifications/hooks/useNotifyConfig";
import { ButtonGroup } from "../../buttonGroup";
import NotifyWizardStep from "../../notifyStep/NotifyWizardStep/NotifyWizardStep";
import TriggerableConfirmationModal from "../../triggeredConfirmationModal/TriggerableConfirmationModal";

export interface NotificationModalProps {
  onTriggerPublishContentObserver: IObservable<(onConfirm: () => void, entityState: NotificationModalState) => void>;
  onConfirmAction: (notificationConfirmPayload: NotifyStepConfirmPayload) => void;
  onClose?: () => void;
  disableNotifications?: boolean;
}

export interface NotificationModalState {
  templateType: TemplateTypes;
  notifyConfigType?: NotifyStepConfig;
  entityType?: SwitchEntityTypes;
}

export const NotificationModal: FC<NotificationModalProps> = (props) => {
  const controllerRef = useRef<NotifyByEmailController>();

  const [isDataValid, setIsDataValid] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [entityState, setEntityState] = useState<NotificationModalState>();

  const { config, NotifyConfigSwitch } = getNotifyConfig(entityState?.notifyConfigType || NotifyStepConfig.Default);
  const [notifyConfig, { setNotifyConfig, resetNotifyConfig, shouldNotify, communicationChannel, notifyTypes }] =
    useNotifyConfig<any>(config);

  const handleCommunicationChannelChange = useCallback(
    (value: string) => setNotifyConfig({ key: "communicationChannel", value: tabs.indexOf(value) }),
    [setNotifyConfig],
  );

  const onNotifyStepLoaded = (controller: NotifyByEmailController) => {
    controllerRef.current = controller;
    setIsDataLoaded(true);
  };

  const onResetNotifyStep = () => {
    controllerRef.current?.resetState();
    resetNotifyConfig();
    props.onClose?.();
  };

  const onConfirmed = async () => {
    const notificationSettings = await controllerRef.current?.getSettings(shouldNotify, communicationChannel)!;
    props.onConfirmAction({ notificationSettings, notifyTypes });
    onResetNotifyStep();
  };

  const modalContent = useMemo(() => {
    return (
      <NotifyWizardStep
        templateType={entityState?.templateType || TemplateTypes.Empty}
        onLoaded={onNotifyStepLoaded}
        onValidChange={setIsDataValid}
        shouldNotify={shouldNotify}
        communicationChannel={communicationChannel}
        isDisabled={props.disableNotifications}
        renderSwitch={(switchProps) => (
          <div>
            <NotifyConfigSwitch
              config={notifyConfig}
              onNotifyConfigChange={setNotifyConfig}
              entityType={entityState?.entityType || SwitchEntityTypes.Empty}
              {...switchProps}
            />
            {entityState?.notifyConfigType === NotifyStepConfig.WithProgress && (
              <ButtonGroup
                items={tabs}
                defaultValue={tabs[communicationChannel]}
                isDisabled={
                  !notifyConfig.shouldNotifyNotStarted &&
                  !notifyConfig.shouldNotifyInProgress &&
                  !notifyConfig.shouldNotifyCompleted
                }
                onChange={handleCommunicationChannelChange}
              />
            )}
          </div>
        )}
      />
    );
  }, [
    entityState,
    shouldNotify,
    communicationChannel,
    NotifyConfigSwitch,
    notifyConfig,
    setNotifyConfig,
    handleCommunicationChannelChange,
    props.disableNotifications,
  ]);

  return (
    <TriggerableConfirmationModal
      title="Notify"
      size={ModalSizes.large}
      confirmButtonLabel="Finish"
      content={modalContent}
      disabled={shouldNotify && !(isDataValid && isDataLoaded)}
      onTriggerModalObserver={props.onTriggerPublishContentObserver}
      onTrigger={setEntityState}
      onConfirmed={onConfirmed}
      onClose={onResetNotifyStep}
    />
  );
};

export default NotificationModal;
